import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ResultsViewProps } from '../../engine/types';
import { useCalculator } from '../../engine/state/CalculatorProvider';
import type { HsaSavingsValues } from './config';
import type { HsaSavingsResult } from './calculate';
import { BarChart, formatDollarPrecise } from './BarChart';
import { usePlanTypeChange } from './PlanTypeField';
import { ResetButton } from './ResetButton';
import { Odometer } from '../../engine/chrome/Odometer';

/**
 * Plan-type gate (Brad, 2026-09-28): on load the only thing shown is a large,
 * centered plan-type choice. Picking one shrinks it away upward into a one-line
 * "Choose your plan type" link that shares the top row with RESET CALCULATOR,
 * and the chart + fields slide in. Clicking that link slides them back out and
 * brings the chooser back.
 *
 *   gate      chooser visible; chart, fields and top row hidden
 *   gate-out  chooser animating away (GATE_MS), then -> open
 *   open      top row + chart + fields visible (slide-in animation)
 *   open-out  chart + fields sliding out (GATE_MS), then -> gate
 *
 * The phase is mirrored onto the .hsab-calc root as data-gate so the stylesheet
 * can also hide/animate the engine's step panel (the six fields), which is a
 * sibling of this view rather than a child. data-gate-moving is set for the
 * whole enter+exit window (lets the slide clip horizontal overflow only while
 * moving), and data-gate-returned once the user has come back to the chooser
 * (so the chooser's enter animation doesn't play on first page load).
 *
 * The motion itself follows the calculator's Animation setting (root
 * data-animation): fade (default), slide, or none.
 */
type GatePhase = 'gate' | 'gate-out' | 'open' | 'open-out';

/** Exit animation length; must match the stylesheet's gate keyframes. */
const GATE_MS = 300;
/** Longest enter animation incl. the fields' stagger delay (0.45s + 0.08s). */
const ENTER_MS = 530;

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

const PLAN_OPTIONS: { value: HsaSavingsValues['planType']; label: string }[] = [
  { value: 'individual', label: 'Individual' },
  { value: 'family', label: 'Family' },
];

/**
 * "Spotlight" layout (Brad, 2026-09-28, content team's Design B). Renders
 * before the step panel (config.resultsOrder: 'before'). Totals live inside
 * the chart card instead of in separate boxes, and the final-year bar is
 * highlighted with its value always visible.
 *
 * Totals sit in a header row above the plot, not floating over it as in the
 * mockup: the y-axis top is always the next bracket above the final value, so
 * tall right-hand bars would run into text placed inside the plot area.
 */
/**
 * Invisible text each total reserves room for (Brad, 2026-09-29: the totals
 * must never move). Without it, a total gaining a digit ($99,999 -> $100,000)
 * widened its box and re-centered the whole centered pair, shifting the other
 * total too. Eight digits plus cents covers the largest value the inputs allow (~$10.3M:
 * family max contribution, no expenses, 30 years at 20%). Measured from real
 * text rather than a ch width, so it fits whatever font the site uses.
 */
const TOTAL_SIZER = formatDollarPrecise(88_888_888);

export function HsaSavingsSpotlightResultsView({
  result,
  values,
  setValue,
  reset,
}: ResultsViewProps<HsaSavingsValues, HsaSavingsResult>) {
  const changePlanType = usePlanTypeChange(values, setValue);
  const { controlStyle } = useCalculator();
  const [phase, setPhase] = useState<GatePhase>('gate');
  const [moving, setMoving] = useState(false);
  const [returned, setReturned] = useState(false);
  const [revealCount, setRevealCount] = useState(0);
  // Nothing looks selected on first load (planType still defaults to
  // 'individual' underneath); after a pick, the chooser shows the current one.
  const [hasChosen, setHasChosen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const changeBtnRef = useRef<HTMLButtonElement>(null);
  const gateBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const timerRef = useRef<number | undefined>(undefined);
  // Where focus should land once the next phase has rendered.
  const focusNextRef = useRef<'change' | 'gate' | null>(null);

  // Mirror onto the calculator root before paint, so the fields never flash
  // visible on first load.
  useLayoutEffect(() => {
    const calcRoot = rootRef.current?.closest<HTMLElement>('.hsab-calc');
    if (!calcRoot) return;
    calcRoot.dataset.gate = phase;
    if (moving) calcRoot.dataset.gateMoving = '';
    else delete calcRoot.dataset.gateMoving;
    if (returned) calcRoot.dataset.gateReturned = '';
    else delete calcRoot.dataset.gateReturned;
  }, [phase, moving, returned]);

  // Count-up restarts each time the calculator comes into view (phase
  // 'open'), not at click time, so it doesn't run unseen behind the
  // chooser's exit animation.
  useEffect(() => {
    if (phase === 'open') setRevealCount((n) => n + 1);
  }, [phase]);

  useEffect(() => {
    if (phase === 'open' && focusNextRef.current === 'change') {
      changeBtnRef.current?.focus();
      focusNextRef.current = null;
    } else if (phase === 'gate' && focusNextRef.current === 'gate') {
      gateBtnRefs.current[values.planType]?.focus();
      focusNextRef.current = null;
    }
  }, [phase, values.planType]);

  const movingTimerRef = useRef<number | undefined>(undefined);
  useEffect(
    () => () => {
      window.clearTimeout(timerRef.current);
      window.clearTimeout(movingTimerRef.current);
    },
    [],
  );

  const transition = (via: GatePhase, to: GatePhase, focus: 'change' | 'gate') => {
    window.clearTimeout(timerRef.current);
    window.clearTimeout(movingTimerRef.current);
    focusNextRef.current = focus;
    if (controlStyle.animation === 'none' || prefersReducedMotion()) {
      setMoving(false);
      setPhase(to);
      return;
    }
    setMoving(true);
    setPhase(via);
    timerRef.current = window.setTimeout(() => setPhase(to), GATE_MS);
    movingTimerRef.current = window.setTimeout(() => setMoving(false), GATE_MS + ENTER_MS);
  };

  const choose = (plan: HsaSavingsValues['planType']) => {
    changePlanType(plan);
    setHasChosen(true);
    transition('gate-out', 'open', 'change');
  };

  const backToGate = () => {
    setReturned(true);
    transition('open-out', 'gate', 'gate');
  };

  const handleReset = () => {
    reset();
    setHasChosen(false);
    backToGate();
  };

  const planLabel = PLAN_OPTIONS.find((o) => o.value === values.planType)?.label ?? '';

  return (
    <div className="hsab-calc-savings__results hsab-calc-savings__results--spotlight" ref={rootRef}>
      <div className="hsab-calc-savings__gate" role="group" aria-labelledby="hsab-calc-savings-gate-title">
        <h2 className="hsab-calc-savings__gate-title" id="hsab-calc-savings-gate-title">
          Choose your plan type
        </h2>
        <div className="hsab-calc-savings__gate-options">
          {PLAN_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              ref={(el) => {
                gateBtnRefs.current[opt.value] = el;
              }}
              className="hsab-calc-savings__gate-option"
              aria-pressed={hasChosen && values.planType === opt.value}
              onClick={() => choose(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="hsab-calc-savings__spot-top">
        <button
          type="button"
          ref={changeBtnRef}
          className="hsab-calc-savings__change-plan"
          onClick={backToGate}
          aria-label={`Choose your plan type. Currently ${planLabel}.`}
        >
          <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" focusable="false">
            <path
              d="M12 19V5M5.5 11.5 12 5l6.5 6.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="hsab-calc-savings__change-plan-label">Choose your plan type</span>
          <span className="hsab-calc-savings__change-plan-current">{planLabel}</span>
        </button>
        <ResetButton onClick={handleReset} />
      </div>

      <div className="hsab-calc-savings__spot-body">
        <div className="hsab-calc-savings__spot-card">
          <div className="hsab-calc-savings__spot-totals">
            <div className="hsab-calc-savings__spot-totals-grid">
              <div className="hsab-calc-savings__spot-total">
                <div className="hsab-calc-savings__spot-total-label">
                  Future Value of Your
                  <br />
                  Health Savings Account:
                </div>
                <div className="hsab-calc-savings__spot-total-value">
                  <span className="hsab-calc-savings__spot-total-sizer" aria-hidden="true">
                    {TOTAL_SIZER}
                  </span>
                  <span className="hsab-calc-savings__spot-total-live">
                    <Odometer
                      key={revealCount}
                      value={result.totalSavings}
                      format={formatDollarPrecise}
                      firstMs={1000}
                    />
                  </span>
                </div>
              </div>
              <div className="hsab-calc-savings__spot-total">
                <div className="hsab-calc-savings__spot-total-label">
                  *Tax-Savings on
                  <br />
                  Tax-Deferred Growth:
                </div>
                <div className="hsab-calc-savings__spot-total-value">
                  <span className="hsab-calc-savings__spot-total-sizer" aria-hidden="true">
                    {TOTAL_SIZER}
                  </span>
                  <span className="hsab-calc-savings__spot-total-live">
                    <Odometer
                      key={revealCount}
                      value={result.taxSavings}
                      format={formatDollarPrecise}
                      firstMs={1000}
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>

          <BarChart result={result} spotlight />
        </div>

        {result.noSavings && (
          <p className="hsab-calc-savings__no-savings-msg">
            The amount of average yearly medical expenses is greater than average yearly contributions, so no
            savings will accumulate.
          </p>
        )}

        {/* 2026-10-08: footnote moved out of the calculator into a separate Text
            component on the page (content team), so it isn't shown twice. Kept to restore. */}
        {/*
        <p className="hsab-calc-savings__footnote">
          *Estimate of potential income tax savings from HSA contributions and earnings.
        </p>
        */}
      </div>
    </div>
  );
}
